import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import DateTimePicker from "./DateTimePicker";
import SearchableSelect from "./SearchableSelect";
import VoiceTextarea from "./VoiceTextarea";

/**
 * Reusable meeting form — used by Calendar `+ Meeting` AND Leads `+ Meeting`
 * popovers. Lets the user attach the meeting to a customer, site, lead, or
 * none.
 *
 * Props:
 *   onCreated(meeting)      — called after successful create
 *   onCancel()              — close the form
 *   defaultCustomerId       — pre-fill (e.g. when invoked from a lead row)
 *   defaultLeadId           — pre-fill
 *   defaultSiteId           — pre-fill
 *   compact = false         — skip the title row
 */
export default function MeetingForm({
  onCreated,
  onCancel,
  defaultCustomerId = "",
  defaultLeadId = "",
  defaultSiteId = "",
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    customer_id: defaultCustomerId,
    site_id: defaultSiteId,
    lead_id: defaultLeadId,
    start: "",
    end: "",
    attendees: "",
  });
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [sites, setSites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [custQuery, setCustQuery] = useState("");

  // Initial load: customers + leads (small + filtered later)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, l] = await Promise.all([
          api.get("/customers"),
          api.get("/leads"),
        ]);
        if (cancelled) return;
        setCustomers(c.data || []);
        setLeads(l.data || []);
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sites depend on the chosen customer
  useEffect(() => {
    if (!form.customer_id) { setSites([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/sites?customer_id=${form.customer_id}`);
        if (!cancelled) setSites(data || []);
      } catch (_e) {
        if (!cancelled) setSites([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.customer_id]);

  // If a lead is chosen first, infer the customer
  useEffect(() => {
    if (!form.lead_id) return;
    const lead = leads.find((l) => l.id === form.lead_id);
    if (lead?.customer_id && !form.customer_id) {
      setForm((f) => ({ ...f, customer_id: lead.customer_id }));
    }
  }, [form.lead_id, leads, form.customer_id]);

  const matchingCustomers = useMemo(() => {
    const q = custQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 50);
    return customers
      .filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [customers, custQuery]);

  const matchingLeads = useMemo(() => {
    const cid = form.customer_id;
    return leads.filter((l) => !cid || l.customer_id === cid).slice(0, 50);
  }, [leads, form.customer_id]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        customer_id: form.customer_id || null,
        site_id: form.site_id || null,
        lead_id: form.lead_id || null,
        start: form.start,
        end: form.end || form.start,
        attendees: form.attendees
          ? form.attendees.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      const { data } = await api.post("/meetings", body);
      onCreated?.(data);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail, "Could not create meeting"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="meeting-form">
      {err && <div className="text-xs text-rose-300 px-1" data-testid="meeting-form-err">{err}</div>}

      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Title</label>
        <input
          required
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Site visit / discovery call / proposal review…"
          className="hv-input mt-1"
          data-testid="meeting-input-title"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Description (optional)</label>
        <VoiceTextarea
          value={form.description}
          onChange={(v) => setForm({ ...form, description: v })}
          rows={3}
          className="mt-1 text-sm"
          placeholder="Agenda, context, what to bring. Or tap the mic to dictate."
          context="lead_activity"
          testIdPrefix="meeting-desc-voice"
          data-testid="meeting-input-description"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Start</label>
          <DateTimePicker
            value={form.start}
            onChange={(v) => setForm({ ...form, start: v })}
            testId="meeting-input-start"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">End</label>
          <DateTimePicker
            value={form.end}
            onChange={(v) => setForm({ ...form, end: v })}
            testId="meeting-input-end"
          />
        </div>
      </div>

      <div className="border-t border-white/5 pt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] mb-2">Link to (optional)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Customer</label>
            <SearchableSelect
              value={form.customer_id || ""}
              onChange={(id) => setForm({ ...form, customer_id: id, site_id: "" })}
              options={customers.map((c) => ({
                id: c.id,
                label: c.name,
                sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.vat_number && `VAT ${c.vat_number}`, c.phone, c.email].filter(Boolean).join(" · "),
                searchTerms: [c.name, c.external_ref, c.vat_number, c.phone, c.whatsapp, c.email, c.industry, c.physical_address, c.legal_name, c.short_id].filter(Boolean),
              }))}
              placeholder="— No customer —"
              testId="meeting-input-customer"
              emptyHint="No customers match. Try VAT, account no., phone or email."
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Site</label>
            <SearchableSelect
              value={form.site_id || ""}
              onChange={(id) => setForm({ ...form, site_id: id })}
              options={sites.map((s) => ({
                id: s.id,
                label: s.name,
                sublabel: s.address || "",
                searchTerms: [s.name, s.address, s.external_ref].filter(Boolean),
              }))}
              placeholder={form.customer_id ? "— No specific site —" : "Pick a customer first"}
              disabled={!form.customer_id}
              testId="meeting-input-site"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Lead</label>
            <SearchableSelect
              value={form.lead_id || ""}
              onChange={(id) => setForm({ ...form, lead_id: id })}
              options={matchingLeads.map((l) => ({
                id: l.id,
                label: l.title || l.contact_name || "Untitled lead",
                sublabel: l.customer_name || "",
                searchTerms: [l.title, l.contact_name, l.customer_name, l.industry, l.phase].filter(Boolean),
              }))}
              placeholder="— No lead —"
              testId="meeting-input-lead"
            />
            <div className="text-[10px] text-[#647087] mt-1">
              Linking to a closed lead reopens it onto the <span className="text-[#1FB6C1]">Callout / Meeting Scheduled</span> phase.
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Attendees (comma-separated emails)</label>
        <input
          value={form.attendees}
          onChange={(e) => setForm({ ...form, attendees: e.target.value })}
          placeholder="rep@yourcompany.com, customer@example.com"
          className="hv-input mt-1 text-sm"
          data-testid="meeting-input-attendees"
        />
      </div>

      <div className="flex gap-2 pt-1">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="hv-btn hv-btn-ghost flex-1"
            data-testid="meeting-cancel"
          >Cancel</button>
        )}
        <button
          type="submit"
          disabled={busy || !form.title || !form.start}
          className="hv-btn hv-btn-primary flex-1"
          data-testid="meeting-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create meeting"}
        </button>
      </div>
    </form>
  );
}
